import { useState, useEffect } from 'react';
import styles from './DashboardVendas.module.css';
import ProductImageTooltip from '../ProductImageTooltip/ProductImageTooltip';
import DatePicker from '../DatePicker/DatePicker';

const DashboardVendas = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [resumo, setResumo] = useState(null);
    const [vendasPorDia, setVendasPorDia] = useState([]);
    const [produtosMaisVendidos, setProdutosMaisVendidos] = useState([]);
    const [periodo, setPeriodo] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [lojaFiltro, setLojaFiltro] = useState('todas');

    useEffect(() => {
        carregarDados();
    }, [periodo, lojaFiltro]);

    const carregarDados = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                startDate: periodo.startDate,
                endDate: periodo.endDate
            });

            // Carregar resumo de vendas
            const resumoResponse = await fetch(`/api/dashboard/vendas-resumo?${params}`);
            if (!resumoResponse.ok) throw new Error('Erro ao carregar resumo de vendas');
            const resumoData = await resumoResponse.json();
            setResumo(resumoData.data);

            // Carregar vendas por dia
            const vendasDiaParams = new URLSearchParams({
                ...Object.fromEntries(params),
                loja: lojaFiltro
            });
            const vendasDiaResponse = await fetch(`/api/dashboard/vendas-por-dia?${vendasDiaParams}`);
            if (!vendasDiaResponse.ok) throw new Error('Erro ao carregar vendas por dia');
            const vendasDiaData = await vendasDiaResponse.json();
            setVendasPorDia(vendasDiaData.data);

            // Carregar produtos mais vendidos
            const produtosParams = new URLSearchParams({
                ...Object.fromEntries(params),
                loja: lojaFiltro,
                limit: 10
            });
            const produtosResponse = await fetch(`/api/dashboard/produtos-mais-vendidos?${produtosParams}`);
            if (!produtosResponse.ok) throw new Error('Erro ao carregar produtos mais vendidos');
            const produtosData = await produtosResponse.json();
            setProdutosMaisVendidos(produtosData.data);

        } catch (err) {
            console.error('Erro ao carregar dados do dashboard:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    const formatarData = (data) => {
        return new Date(data).toLocaleDateString('pt-BR');
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>
                    <div className={styles.spinner}></div>
                    <p>Carregando dados do dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.error}>
                    <h2>❌ Erro ao carregar dados</h2>
                    <p>{error}</p>
                    <button onClick={carregarDados} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* Cabeçalho */}
            <div className={styles.header}>
                <h1>📊 Dashboard de Vendas</h1>
                <p className={styles.subtitle}>
                    Visão consolidada das vendas das lojas e e-commerce
                </p>
            </div>

            {/* Filtros */}
            <div className={styles.filters}>
                <div className={styles.filterGroup}>
                    <label>Data Início:</label>
                    <DatePicker
                        value={periodo.startDate}
                        onChange={(valor) => setPeriodo({ ...periodo, startDate: valor })}
                        placeholder="Data início"
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label>Data Fim:</label>
                    <DatePicker
                        value={periodo.endDate}
                        onChange={(valor) => setPeriodo({ ...periodo, endDate: valor })}
                        placeholder="Data fim"
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label>Loja:</label>
                    <select
                        value={lojaFiltro}
                        onChange={(e) => setLojaFiltro(e.target.value)}
                        className={styles.select}
                    >
                        <option value="todas">Todas</option>
                        <option value="centro">Loja Centro</option>
                        <option value="caxias">Loja Caxias</option>
                        <option value="ecommerce">E-commerce</option>
                    </select>
                </div>
                <button onClick={carregarDados} className={styles.refreshButton}>
                    🔄 Atualizar
                </button>
            </div>

            {/* Cards de Resumo */}
            {resumo && (
                <div className={styles.cardsContainer}>
                    {/* Loja Centro */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <span className={styles.cardIcon}>🏪</span>
                            <h3>Loja Centro</h3>
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Vendas:</span>
                                <span className={styles.metricValue}>{resumo.lojaCentro.totalVendas}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Quantidade:</span>
                                <span className={styles.metricValue}>{resumo.lojaCentro.totalQuantidade}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Valor Total:</span>
                                <span className={styles.metricValue}>{formatarMoeda(resumo.lojaCentro.valorTotal)}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Ticket Médio:</span>
                                <span className={styles.metricValue}>{formatarMoeda(resumo.lojaCentro.ticketMedio)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Loja Caxias */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <span className={styles.cardIcon}>🏬</span>
                            <h3>Loja Caxias</h3>
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Vendas:</span>
                                <span className={styles.metricValue}>{resumo.lojaCaxias.totalVendas}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Quantidade:</span>
                                <span className={styles.metricValue}>{resumo.lojaCaxias.totalQuantidade}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Valor Total:</span>
                                <span className={styles.metricValue}>{formatarMoeda(resumo.lojaCaxias.valorTotal)}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Ticket Médio:</span>
                                <span className={styles.metricValue}>{formatarMoeda(resumo.lojaCaxias.ticketMedio)}</span>
                            </div>
                        </div>
                    </div>

                    {/* E-commerce */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <span className={styles.cardIcon}>🛒</span>
                            <h3>E-commerce</h3>
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Vendas:</span>
                                <span className={styles.metricValue}>{resumo.ecommerce.totalVendas}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Quantidade:</span>
                                <span className={styles.metricValue}>{resumo.ecommerce.totalQuantidade}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Valor Total:</span>
                                <span className={styles.metricValue}>{formatarMoeda(resumo.ecommerce.valorTotal)}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Ticket Médio:</span>
                                <span className={styles.metricValue}>{formatarMoeda(resumo.ecommerce.ticketMedio)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Total Geral */}
                    <div className={`${styles.card} ${styles.cardTotal}`}>
                        <div className={styles.cardHeader}>
                            <span className={styles.cardIcon}>💰</span>
                            <h3>Total Geral</h3>
                        </div>
                        <div className={styles.cardContent}>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Vendas:</span>
                                <span className={styles.metricValue}>{resumo.total.totalVendas}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Quantidade:</span>
                                <span className={styles.metricValue}>{resumo.total.totalQuantidade}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Valor Total:</span>
                                <span className={styles.metricValue}>{formatarMoeda(resumo.total.valorTotal)}</span>
                            </div>
                            <div className={styles.metric}>
                                <span className={styles.metricLabel}>Ticket Médio:</span>
                                <span className={styles.metricValue}>{formatarMoeda(resumo.total.ticketMedio)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Produtos Mais Vendidos */}
            {produtosMaisVendidos.length > 0 && (
                <div className={styles.section}>
                    <h2>🏆 Produtos Mais Vendidos</h2>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Código</th>
                                    <th>Descrição</th>
                                    <th>Loja</th>
                                    <th>Quantidade</th>
                                    <th>Valor Total</th>
                                    <th>Ticket Médio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {produtosMaisVendidos.map((produto, index) => (
                                    <tr key={`${produto.codigoProduto}-${produto.loja}-${index}`}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <ProductImageTooltip
                                                codigo={produto.codigoProduto}
                                                tipo={produto.loja.toLowerCase() === 'e-commerce' ? 'ecommerce' : 'loja-fisica'}
                                            >
                                                <span className={styles.codigoComTooltip}>{produto.codigoProduto}</span>
                                            </ProductImageTooltip>
                                        </td>
                                        <td>{produto.descricao}</td>
                                        <td>
                                            <span className={styles.lojaBadge} data-loja={produto.loja.toLowerCase()}>
                                                {produto.loja}
                                            </span>
                                        </td>
                                        <td>{produto.totalQuantidade}</td>
                                        <td>{formatarMoeda(produto.valorTotal)}</td>
                                        <td>{formatarMoeda(produto.ticketMedio)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Vendas por Dia */}
            {vendasPorDia.length > 0 && (
                <div className={styles.section}>
                    <h2>📅 Vendas por Dia</h2>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Loja</th>
                                    <th>Vendas</th>
                                    <th>Quantidade</th>
                                    <th>Valor Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vendasPorDia.slice(0, 50).map((venda, index) => (
                                    <tr key={`${venda.dia}-${venda.loja}-${index}`}>
                                        <td>{formatarData(venda.dia)}</td>
                                        <td>
                                            <span className={styles.lojaBadge} data-loja={venda.loja.toLowerCase()}>
                                                {venda.loja}
                                            </span>
                                        </td>
                                        <td>{venda.totalVendas}</td>
                                        <td>{venda.totalQuantidade}</td>
                                        <td>{formatarMoeda(venda.valorTotal)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardVendas;
